import { decodeActionCard, getActionCardKind } from '@littlebird/action-cards';
import { extractMarkdownLinkSpans } from '@littlebird/markdown-utils';
import {
  type ActionCard,
  ChatStatus,
  type Message,
  ModalityType,
} from '@littlebird/sdk';
import {
  deriveLoaderActivity,
  findLinkedActionCardIds,
  formatCallIntegrationLabel,
  isSelfToolExecute,
  mapToolNameToLabel,
  normalizeIntegrationName,
  opensTurn,
  parseActionCardUrl,
  type ReasoningModalityLike,
  type ReasoningStep,
  ReasoningStepType,
} from '@littlebird/ui-utils';
import { differenceInCalendarDays } from 'date-fns/differenceInCalendarDays';
import { format } from 'date-fns/format';
import { startOfDay } from 'date-fns/startOfDay';

import { FEATURES, hasFeature } from '@/features/feature-flags';
import { Routes } from '@/types/routes';
import { parseOAuthUrl } from '@/utils/oauth-url-parser';
import { isSystemNote } from '@/utils/system-note';

export const isAgentsUxEnabled = () => hasFeature(FEATURES.AGENTS_UX);

const AGENTS_ONLY_ROUTES = [
  Routes.AgentsNewTask,
  Routes.AgentsAttention,
] as const;

export const isAgentsOnlyRoute = (pathname: string) =>
  AGENTS_ONLY_ROUTES.some((route) => pathname.startsWith(route));

const AGENT_PRIMING_MARKER = '[agent-setup]';

export const isPendingAgentCard = (card: ActionCard) =>
  !card.deletedAt &&
  card.status === 'draft' &&
  (card.actionType === 'agent_team' ||
    card.actionType === 'informational' ||
    (!getActionCardKind(card.actionType) && decodeActionCard(card) !== null) ||
    decodeActionCard(card)?.capability === 'confirmable');

export type AgentPendingGroup<TAgent> = {
  agent: TAgent;
  cards: ActionCard[];
};

export const groupPendingCardsByAgent = <TAgent extends { agentId: string }>(
  cards: readonly ActionCard[],
  agents: readonly TAgent[],
  agentForCard: (card: ActionCard) => TAgent | undefined
): AgentPendingGroup<TAgent>[] => {
  const cardsByAgentId = new Map<string, ActionCard[]>();
  for (const card of cards) {
    const agent = agentForCard(card);
    if (!agent) continue;
    const group = cardsByAgentId.get(agent.agentId) ?? [];
    group.push(card);
    cardsByAgentId.set(agent.agentId, group);
  }
  const latestMs = (agentId: string) =>
    cardsByAgentId.get(agentId)?.[0]?.createdAt.getTime() ?? 0;
  return agents
    .map((agent) => ({ agent, cards: cardsByAgentId.get(agent.agentId) ?? [] }))
    .sort((a, b) => latestMs(b.agent.agentId) - latestMs(a.agent.agentId));
};

export type DayGroup<T> = { day: Date; items: T[] };

export const groupByDay = <T>(
  items: readonly T[],
  dateOf: (item: T) => Date
): DayGroup<T>[] => {
  const groups: DayGroup<T>[] = [];
  for (const item of items) {
    const day = startOfDay(dateOf(item));
    const last = groups.at(-1);
    if (last && last.day.getTime() === day.getTime()) last.items.push(item);
    else groups.push({ day, items: [item] });
  }
  return groups;
};

export const formatDayLabel = (day: Date, now = new Date()) => {
  const daysApart = differenceInCalendarDays(now, day);
  if (daysApart <= 0) return 'Today';
  if (daysApart === 1) return 'Yesterday';
  if (daysApart < 7) return format(day, 'EEEE');
  return format(day, 'MMM d');
};

type TimelineMessage = Pick<Message, 'id' | 'role' | 'createdAt'> &
  Partial<Pick<Message, 'joinedTurn'>>;

export const groupCardsByMessage = (
  messages: readonly TimelineMessage[],
  renderedReplyIds: ReadonlySet<string>,
  cards: readonly ActionCard[],
  {
    replyPending = false,
    olderMessagesUnloaded = false,
    linkedHosts = new Map<number, string>(),
  }: {
    replyPending?: boolean;
    olderMessagesUnloaded?: boolean;
    linkedHosts?: ReadonlyMap<number, string>;
  } = {}
) => {
  const turnStartFor = (card: ActionCard) => {
    const anchorIndex = messages.findIndex(
      (message) => message.id === card.assistantMessageId
    );
    if (anchorIndex >= 0) return anchorIndex;
    const laterIndex = messages.findIndex(
      (message) => message.createdAt >= card.createdAt
    );
    if (laterIndex === 0 && olderMessagesUnloaded) return undefined;
    return laterIndex >= 0 ? laterIndex : messages.length;
  };
  const hostFor = (card: ActionCard) => {
    const linkedHost = linkedHosts.get(card.localId);
    if (linkedHost !== undefined) return linkedHost;
    if (!card.assistantMessageId) return undefined;
    if (renderedReplyIds.has(card.assistantMessageId))
      return card.assistantMessageId;
    const start = turnStartFor(card);
    if (start === undefined) return undefined;
    let index = start;
    for (; index < messages.length; index += 1) {
      if (opensTurn(messages[index])) break;
      if (renderedReplyIds.has(messages[index].id)) return messages[index].id;
    }
    const turnIsLiveTail = index === messages.length;
    if (replyPending && turnIsLiveTail) return undefined;
    for (let i = start - 1; i >= 0; i -= 1) {
      if (opensTurn(messages[i])) break;
      if (renderedReplyIds.has(messages[i].id)) return messages[i].id;
    }
    return undefined;
  };
  const grouped = new Map<string, ActionCard[]>();
  for (const card of cards) {
    const host = hostFor(card);
    if (!host) continue;
    grouped.set(host, [...(grouped.get(host) ?? []), card]);
  }
  return grouped;
};

const AGENT_PROFILE_UPDATE_PREFIX = '<system kind="agent_profile_update">';

export const isAgentProfileUpdateMessage = (message: {
  role: string;
  content: string | null | undefined;
}) =>
  message.role === 'user' &&
  (message.content?.trimStart() ?? '').startsWith(AGENT_PROFILE_UPDATE_PREFIX);

export const isAgentPrimingMessage = (message: {
  role: string;
  content: string | null | undefined;
}) =>
  isSystemNote(message) ||
  (message.role === 'user' &&
    (message.content?.trimStart() ?? '').startsWith(AGENT_PRIMING_MARKER));

const ACTION_CARD_PATH = '/action-cards/';
const TAIL_REMAINDER = /^[\s.,;:!?]*$/;
const LIST_OR_QUOTE_START = /^\s*(?:[-*+]\s|\d+[.)]\s|>)/;

const actionCardTails = (markdown: string) => {
  const tails = new Map<number, { head: string; tail: string }>();
  if (!markdown.includes(ACTION_CARD_PATH)) return tails;
  const lineStarts = [0];
  for (let i = 0; i < markdown.length; i += 1) {
    if (markdown[i] === '\n') lineStarts.push(i + 1);
  }
  const lineOf = (offset: number) => {
    let low = 0;
    let high = lineStarts.length - 1;
    while (low < high) {
      const mid = (low + high + 1) >> 1;
      if (lineStarts[mid] <= offset) low = mid;
      else high = mid - 1;
    }
    return low;
  };
  const lines = new Map<number, { blanked: string; firstSpan: number }>();
  for (const span of extractMarkdownLinkSpans(markdown)) {
    if (parseActionCardUrl(span.href).kind !== 'actionCard') continue;
    const line = lineOf(span.start);
    if (lineOf(span.end - 1) !== line) continue;
    const start = lineStarts[line];
    const end =
      line + 1 < lineStarts.length ? lineStarts[line + 1] - 1 : markdown.length;
    const entry = lines.get(line) ?? {
      blanked: markdown.slice(start, end),
      firstSpan: span.start - start,
    };
    entry.blanked =
      entry.blanked.slice(0, span.start - start) +
      ' '.repeat(span.end - span.start) +
      entry.blanked.slice(span.end - start);
    entry.firstSpan = Math.min(entry.firstSpan, span.start - start);
    lines.set(line, entry);
  }
  for (const [line, { blanked, firstSpan }] of lines) {
    if (!TAIL_REMAINDER.test(blanked.slice(firstSpan))) continue;
    const head = blanked.slice(0, firstSpan);
    if (LIST_OR_QUOTE_START.test(head)) continue;
    const start = lineStarts[line];
    const text = markdown.slice(start, start + blanked.length);
    tails.set(line, { head, tail: text.slice(firstSpan) });
  }
  return tails;
};

export const splitAroundActionCardLines = (markdown: string) => {
  const tails = actionCardTails(markdown);
  if (tails.size === 0) return [markdown];
  const pieces: { lines: string[]; isCard: boolean }[] = [];
  const push = (line: string, isCard: boolean) => {
    const last = pieces[pieces.length - 1];
    if (last && last.isCard === isCard) last.lines.push(line);
    else pieces.push({ lines: [line], isCard });
  };
  markdown.split('\n').forEach((line, index) => {
    const split = tails.get(index);
    if (!split) {
      push(line, false);
      return;
    }
    if (split.head.trim() !== '') push(split.head, false);
    push(split.tail, true);
  });
  return pieces
    .map((piece) => piece.lines.join('\n').trim())
    .filter((piece) => piece !== '');
};

export const actionCardSegmentIds = (segment: string) => {
  if (!segment.includes(ACTION_CARD_PATH)) return null;
  const tails = actionCardTails(segment);
  const lines = segment.split('\n');
  const isOnlyCards = lines.every((line, index) => {
    if (line.trim() === '') return true;
    const split = tails.get(index);
    return split !== undefined && split.head.trim() === '';
  });
  if (!isOnlyCards || tails.size === 0) return null;
  return [...findLinkedActionCardIds(segment)];
};

export const stripActionCardLinks = (markdown: string) => {
  if (!markdown.includes(ACTION_CARD_PATH)) return markdown;
  let output = '';
  let cursor = 0;
  for (const span of extractMarkdownLinkSpans(markdown)) {
    if (parseActionCardUrl(span.href).kind !== 'actionCard') continue;
    const label = span.raw.match(/^\[([^\]]*)\]/)?.[1] ?? '';
    output += markdown.slice(cursor, span.start) + label;
    cursor = span.end;
  }
  return output + markdown.slice(cursor);
};

const RESEARCH_MODALITIES = new Set<string>([
  ModalityType.ChatWithWebSearch,
  ModalityType.ChatWithWebSearchMulti,
  ModalityType.ChatWithWebNavigation,
]);

const CONTEXT_SEARCH_MODALITIES = new Set<string>([
  ModalityType.ChatWithUserContext,
  ModalityType.ChatWithPreviousContext,
  ModalityType.ChatWithNotes,
  ModalityType.SelfKnowledge,
  ModalityType.GetScreenContext,
  ModalityType.WorldModel,
]);

const SHELL_MODALITIES = new Set<string>([
  ModalityType.RunShell,
  ModalityType.Bash,
  ModalityType.RunShellCommand,
  ModalityType.ShellCommand,
  ModalityType.RunSandbox,
]);

const INTEGRATION_MODALITIES = new Set<string>([
  ModalityType.ChatWithIntegrations,
  ModalityType.InternalIntegrations,
]);

const integrationVerb = (raw: unknown): string => {
  if (typeof raw !== 'string' || !raw) return 'working';
  const name = normalizeIntegrationName(raw);
  if (!name) return 'working';
  return `checking ${mapToolNameToLabel(name)}`;
};

const toolExecuteVerb = (modality: ReasoningModalityLike): string => {
  if (isSelfToolExecute(modality)) return 'working';
  const slug =
    typeof modality.tool_slug === 'string' ? modality.tool_slug.trim() : '';
  if (slug) return `checking ${formatCallIntegrationLabel(slug)}`;
  return integrationVerb(modality.integration_name);
};

const verbForModality = (modality: ReasoningModalityLike): string => {
  const type = modality.type ?? '';
  if (RESEARCH_MODALITIES.has(type)) return 'researching';
  if (CONTEXT_SEARCH_MODALITIES.has(type)) return 'searching';
  if (SHELL_MODALITIES.has(type)) return 'working';
  if (type === ModalityType.McpToolCall)
    return integrationVerb(modality.server_slug);
  if (type === ModalityType.ToolExecute) return toolExecuteVerb(modality);
  if (INTEGRATION_MODALITIES.has(type))
    return integrationVerb(modality.tool_name);
  if (type === ModalityType.ImageGeneration) return 'creating an image';
  if (type === ModalityType.Route) return 'thinking';
  return 'working';
};

export const isTurnUsingTool = (
  messages: Pick<Message, 'role' | 'content' | 'modalitiesUsed'>[],
  status: ChatStatus | undefined
) => {
  if (status === ChatStatus.WaitingForClientTools) return true;
  const last = messages.at(-1);
  return (
    last?.role === 'assistant' &&
    last.content.trim().length === 0 &&
    deriveLoaderActivity(last.modalitiesUsed) !== 'think'
  );
};

export const deriveWorkingVerb = (
  activeStep?: ReasoningStep | null
): string => {
  if (!activeStep) return 'working';
  if (activeStep.type === ReasoningStepType.Thought) return 'thinking';
  return verbForModality(activeStep.modality);
};

const BUBBLE_SPLIT_BLOCKERS = [/```/, /<copyable/];
const LIST_ITEM_START = /^[ \t]*(?:[-*+] |\d+[.)] )/;
const INDENTED_START = /^[ \t]+\S/;

const endsInList = (segment: string) => {
  const paragraphs = segment.split(/\n{2,}/);
  let index = paragraphs.length - 1;
  while (index > 0 && INDENTED_START.test(paragraphs[index])) index -= 1;
  return LIST_ITEM_START.test(paragraphs[index]);
};

const continuesList = (previous: string, next: string) => {
  const previousIsList = endsInList(previous);
  if (LIST_ITEM_START.test(next))
    return previousIsList || previous.trimEnd().endsWith(':');
  return previousIsList && INDENTED_START.test(next);
};

export const splitIntoBubbleSegments = (content: string): string[] => {
  if (!content.includes('\n\n')) return [content];
  if (BUBBLE_SPLIT_BLOCKERS.some((blocker) => blocker.test(content))) {
    return [content];
  }
  const segments: string[] = [];
  for (const paragraph of content.split(/\n{2,}/)) {
    if (!paragraph.trim()) continue;
    const previous = segments[segments.length - 1];
    if (previous !== undefined && continuesList(previous, paragraph)) {
      segments[segments.length - 1] = `${previous}\n\n${paragraph}`;
    } else {
      segments.push(paragraph);
    }
  }
  const trimmed = segments.map((segment) => segment.trim());
  return trimmed.length > 1 ? trimmed : [content];
};

export const isConnectionCardSegment = (segment: string) => {
  const spans = extractMarkdownLinkSpans(segment);
  if (spans.length !== 1) return false;
  const [span] = spans;
  if (segment.slice(0, span.start).trim() || segment.slice(span.end).trim())
    return false;
  return parseOAuthUrl(span.href).isOAuthUrl;
};

export const holdStreamedSegments = (
  shown: string[],
  next: string[],
  settled: boolean
) => (!settled && next.length < shown.length ? shown : next);
